import {
    Modal,
    ModalOverlay,
    ModalContent,
    ModalHeader,
    ModalCloseButton,
    ModalBody,
    ModalFooter,
    Flex,
    Skeleton,
    FormControl,
    Input,
    FormLabel,
    List,
    ListItem,
    Button,
    Box,
    Heading,
    InputLeftElement,
    InputGroup,
    Tag,
    TagLabel,
    TagCloseButton
} from '@chakra-ui/react';
import { useTranslation } from 'react-i18next';
import { JButton, JGoogleIcon, JInput, JTypography } from '../../components';
import { headingTheme } from '../../theme/components';
import { useEffect, useState } from 'react';
import InfiniteScroll from 'react-infinite-scroll-component';
import { useQuery } from 'react-query';
import { apiGetMember } from '../../apis/memberGroupApi';
import { useDebounce } from 'use-debounce';

interface ModalAddMemberGroupProps {
    isOpen: boolean;
    onClose: () => void;
    groupMemberDetail: any;
    groupMember: any;
    setGroupMember: (callback: (prev: any) => any) => void;
    submitHandler: {
        mutate: () => void;
        isLoading: boolean;
    };
    mode: 'create' | 'edit';
}

const ModalAddMemberGroup = ({
    isOpen,
    onClose,
    groupMemberDetail,
    groupMember,
    setGroupMember,
    submitHandler,
    mode
}: ModalAddMemberGroupProps) => {
    const { t } = useTranslation();

    const [formMember, setFormMember] = useState('');
    const [debouncedSearchTerm] = useDebounce(formMember, 500);
    const [allMembers, setAllMembers] = useState<any[]>([]);
    const [page, setPage] = useState(1);

    const members = useQuery(
        ['member-group-modal-get-members', page, debouncedSearchTerm],
        () => handleGetMembers(page, debouncedSearchTerm),
        {
            keepPreviousData: true,
            enabled: !!debouncedSearchTerm || page > 1
        }
    );

    async function handleGetMembers(page: number, search: string) {
        return await apiGetMember({
            is_active: 1,
            show: 10,
            page,
            search
        });
    }

    const isLoading = submitHandler.isLoading;

    const title =
        mode === 'create'
            ? t('group_anggota_modal_buat_judul')
            : t('group_anggota_modal_ubah_judul');

    const labelName =
        mode === 'create'
            ? t('group_anggota_modal_buat_label_nama')
            : t('group_anggota_modal_ubah_label_nama');

    const placeholderName =
        mode === 'create'
            ? t('group_anggota_modal_buat_placeholder_nama')
            : t('group_anggota_modal_ubah_placeholder_nama');

    const buttonText =
        mode === 'create'
            ? t('group_anggota_modal_buat_simpan')
            : t('group_anggota_modal_berlakukan_grop_pelanggan_simpan');

    useEffect(() => {
        setAllMembers([]);
        setPage(1);
    }, [debouncedSearchTerm]);

    useEffect(() => {
        if (Array.isArray(members?.data?.data)) {
            setAllMembers((prev) =>
                page === 1 ? members.data.data : [...prev, ...members.data.data]
            );
        }
    }, [members.data]);

    const handleClose = () => {
        setFormMember('');
        setAllMembers([]);
        setPage(1);
        onClose();
    };

    return (
        <Modal isOpen={isOpen} onClose={handleClose} size='xl'>
            <ModalOverlay />
            <ModalContent backgroundColor={'surface'} padding={'24px'}>
                <ModalHeader p='0'>
                    <Heading size='chakra_title_large'>
                        {title}
                    </Heading>
                </ModalHeader>
                <ModalCloseButton top="24px" right="24px" isDisabled={isLoading} />
                <ModalBody
                    as={Flex}
                    flexDirection="column"
                    gap="16px"
                    padding="0px"
                    marginTop="24px"
                >
                    {groupMemberDetail.isLoading ? (
                        <Skeleton height="40px" width="100%" />
                    ) : (
                        <FormControl variant='floating' id="text">
                            <JInput
                                placeholder='  '
                                value={groupMember?.display_name}
                                onChange={(e: any) =>
                                    setGroupMember((prev: any) => ({
                                        ...prev,
                                        display_name: e.target.value
                                    }))
                                }
                                isDisabled={isLoading}
                            />
                            <FormLabel
                                transform={
                                    groupMember?.display_name
                                        ? 'scale(0.85) translateY(-24px)'
                                        : 'unset'
                                }>
                                {labelName}
                            </FormLabel>
                        </FormControl>
                    )}
                    {groupMemberDetail.isLoading ? (
                        <Skeleton height="40px" width="100%" />
                    ) : (
                        <FormControl id="text">
                            <InputGroup>
                                <InputLeftElement pointerEvents="none">
                                    <JGoogleIcon name="search" />
                                </InputLeftElement>
                                <Input
                                    autoComplete="off"
                                    placeholder={`Search ${t('group_anggota_modal_ubah_anggota')}`}
                                    type="text"
                                    value={formMember}
                                    onChange={(e: any) => setFormMember(e.target.value)}
                                    {...headingTheme?.sizes?.chakra_label_large}
                                />
                            </InputGroup>

                            {formMember && (
                                <InfiniteScroll
                                    dataLength={allMembers.length}
                                    next={() => setPage((prev) => prev + 1)}
                                    hasMore={
                                        (members?.data?.metadata?.current_page || 1) <
                                        (members?.data?.metadata?.last_page || 1)
                                    }
                                    loader={null}
                                    scrollableTarget="scrollableMemberList"
                                >
                                    <List
                                        id="scrollableMemberList"
                                        position="absolute"
                                        zIndex="10"
                                        bg="surfacePlus1"
                                        w="100%"
                                        borderRadius="16px"
                                        maxH="150px"
                                        overflow="auto"
                                    >
                                        {allMembers.map((m: any, i: number) => (
                                            <ListItem key={i}>
                                                <Button
                                                    p="12px"
                                                    w="100%"
                                                    variant="ghost"
                                                    borderRadius="16px"
                                                    onClick={() => {
                                                        setGroupMember((prev: any) => {
                                                            const newMember = {
                                                                value: m.id,
                                                                label: m.user.display_name,
                                                            };
                                                            const isExist = prev.members?.some(
                                                                (member: any) => member.value === newMember.value
                                                            );
                                                            if (isExist) return prev;
                                                            return {
                                                                ...prev,
                                                                members: [...(prev.members || []), newMember],
                                                            };
                                                        })
                                                        setFormMember('')
                                                    }}
                                                >
                                                    <Flex w="100%" gap="5px">
                                                        <Heading size="chakra_label_large">
                                                            {m.user.display_name}
                                                        </Heading>
                                                    </Flex>
                                                </Button>
                                            </ListItem>
                                        ))}
                                    </List>
                                </InfiniteScroll>
                            )}
                        </FormControl>
                    )}
                    {groupMember?.members.length > 0 &&
                        <JTypography size="label-medium">
                            {t('group_anggota_modal_ubah_anggota')}
                        </JTypography>
                    }
                    {groupMemberDetail.isLoading ? (
                        <Skeleton height="40px" width="100%" />
                    ) : (
                        Array.isArray(groupMember?.members) && groupMember.members.length > 0 && (
                            <Flex wrap="wrap" gap="8px" mt="6px">
                                {groupMember?.members.map((member: any, idx: number) => (
                                    <Tag
                                        key={member.id || idx}
                                        p='6px 8px'
                                        borderRadius="12px"
                                        bg='surfacePlus4'
                                        border='1px solid'
                                        borderColor='primary'
                                    >
                                        <TagLabel>
                                            <Heading size='chakra_label_large' color='primary'>
                                                {member.label}
                                            </Heading>
                                        </TagLabel>
                                        <TagCloseButton color='primary'
                                            onClick={() =>
                                                setGroupMember((prev: any) => ({
                                                    ...prev,
                                                    members: prev.members.filter((m: any) => m.value !== member.value)
                                                }))
                                            }
                                        />
                                    </Tag>
                                ))}
                            </Flex>
                        ))}
                </ModalBody>
                <ModalFooter gap="33px" padding="0px" marginTop="24px">
                    <JButton
                        margin="0px auto"
                        size="normal"
                        variant="j-primary"
                        onClick={() => submitHandler.mutate()}
                        isLoading={isLoading}
                        isDisabled={groupMemberDetail.isLoading}
                    >
                        {buttonText}
                    </JButton>
                </ModalFooter>
            </ModalContent>
        </Modal>
    );
};

export default ModalAddMemberGroup;
